import { defineComponent, onMounted, ref } from 'vue'
import { getUploadTempSignature } from '../background/api'
import {
  getPlatformContactDetail,
  getPlatformContactList,
  savePlatformContact
} from './api'

const EMPTY_FORM = {
  id: '',
  phone: '',
  email: '',
  qr_code_url: '',
  state: 1
}

function unwrapPayload(res) {
  return res && res.data !== undefined ? res.data : res
}

function extractList(res) {
  const payload = unwrapPayload(res)
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray(payload.data)) return payload.data
  if (payload && Array.isArray(payload.list)) return payload.list
  return []
}

function extractMeta(res) {
  const payload = unwrapPayload(res)
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { total: 0, page: 1, page_size: 10, total_pages: 1 }
  }
  const total = Number(payload.total || 0)
  const page = Number(payload.page || 1)
  const pageSize = Number(payload.page_size || payload.pageSize || 10)
  const totalPages = Number(
    payload.total_pages ||
      payload.totalPages ||
      (total && pageSize ? Math.ceil(total / pageSize) : 1)
  )
  return {
    total: Number.isFinite(total) ? total : 0,
    page: Number.isFinite(page) ? page : 1,
    page_size: Number.isFinite(pageSize) ? pageSize : 10,
    total_pages: Number.isFinite(totalPages) ? Math.max(1, totalPages) : 1
  }
}

function normalizeDetail(res) {
  const payload = unwrapPayload(res)
  if (payload && payload.contact && typeof payload.contact === 'object') {
    return payload.contact
  }
  if (payload && payload.detail && typeof payload.detail === 'object') {
    return payload.detail
  }
  return payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {}
}

function pickQrCodeUrl(row) {
  if (!row || typeof row !== 'object') return ''
  const url = row.qr_code_url ?? row.qrCodeUrl
  return url != null && String(url).trim() !== '' ? String(url) : ''
}

function pickStateLabel(state) {
  if (state === 1 || state === '1') return '启用'
  if (state === 0 || state === '0') return '禁用'
  return '—'
}

function formatDate(value) {
  if (!value) return '—'
  return String(value).replace('T', ' ').slice(0, 19)
}

function buildForm(row) {
  return {
    id: row.id == null ? '' : row.id,
    phone: row.phone || '',
    email: row.email || '',
    qr_code_url: pickQrCodeUrl(row),
    state: row.state == null ? 1 : Number(row.state)
  }
}

export default defineComponent({
  name: 'PlatformContactView',
  setup() {
    const contactList = ref([])
    const loading = ref(false)
    const saving = ref(false)
    const detailLoading = ref(false)

    const currentPage = ref(1)
    const pageSize = ref(10)
    const totalPages = ref(1)
    const total = ref(0)
    const filterState = ref('')

    const modalVisible = ref(false)
    const modalTitle = ref('新增联系人')
    const form = ref({ ...EMPTY_FORM })

    const fileInputRef = ref(null)
    const uploadingImage = ref(false)

    const fetchList = async () => {
      loading.value = true
      try {
        const params = {
          page: currentPage.value,
          page_size: pageSize.value
        }
        if (filterState.value !== '') params.state = Number(filterState.value)
        const response = await getPlatformContactList(params)
        contactList.value = extractList(response)
        const meta = extractMeta(response)
        currentPage.value = meta.page
        pageSize.value = meta.page_size
        totalPages.value = meta.total_pages
        total.value = meta.total
      } catch (error) {
        console.error('获取平台联系人列表失败:', error)
        contactList.value = []
        alert(error.message || '获取平台联系人列表失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    const resetForm = () => {
      form.value = { ...EMPTY_FORM }
    }

    const openCreateModal = () => {
      resetForm()
      modalTitle.value = '新增联系人'
      modalVisible.value = true
    }

    const openEditModal = async (row) => {
      const id = row?.id
      if (id == null) {
        alert('缺少联系人 ID')
        return
      }

      modalTitle.value = `编辑联系人 #${id}`
      modalVisible.value = true
      detailLoading.value = true
      resetForm()

      try {
        const response = await getPlatformContactDetail(id)
        form.value = buildForm(normalizeDetail(response))
      } catch (error) {
        console.error('获取联系人详情失败:', error)
        modalVisible.value = false
        alert(error.message || '获取联系人详情失败，请稍后重试')
      } finally {
        detailLoading.value = false
      }
    }

    const closeModal = () => {
      if (saving.value || uploadingImage.value) return
      modalVisible.value = false
      resetForm()
    }

    const extractPresignedUrl = (signatureResponse) => {
      const source = signatureResponse?.data || signatureResponse || {}
      const presignedUrl = source.url
      if (!presignedUrl) {
        throw new Error('上传凭证缺少 url 字段，请检查接口返回')
      }
      return presignedUrl
    }

    const uploadToCos = async (file) => {
      const signatureResponse = await getUploadTempSignature(file.name)
      const presignedUrl = extractPresignedUrl(signatureResponse)
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream'
        },
        body: file
      })
      if (!uploadResponse.ok) {
        throw new Error(`上传 COS 失败: ${uploadResponse.status}`)
      }
      return presignedUrl.split('?')[0]
    }

    const triggerQrUpload = () => {
      if (uploadingImage.value) return
      if (fileInputRef.value) {
        fileInputRef.value.value = ''
        fileInputRef.value.click()
      }
    }

    const handleSelectQrImage = async (event) => {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      if (!file.type || !file.type.startsWith('image/')) {
        alert('请选择图片文件')
        return
      }

      uploadingImage.value = true
      try {
        form.value.qr_code_url = await uploadToCos(file)
        alert('二维码图片上传成功')
      } catch (error) {
        console.error('上传二维码失败:', error)
        alert(error.message || '上传二维码失败，请稍后重试')
      } finally {
        uploadingImage.value = false
      }
    }

    const handleSave = async () => {
      const payload = {
        state: Number(form.value.state)
      }

      const phone = String(form.value.phone || '').trim()
      const email = String(form.value.email || '').trim()
      const qrCodeUrl = String(form.value.qr_code_url || '').trim()

      if (phone) payload.phone = phone
      if (email) payload.email = email
      if (qrCodeUrl) payload.qr_code_url = qrCodeUrl

      if (form.value.id) {
        payload.id = Number(form.value.id)
      } else if (!phone && !email && !qrCodeUrl) {
        alert('请至少填写电话、邮箱或二维码图片中的一项')
        return
      }

      saving.value = true
      try {
        await savePlatformContact(payload)
        alert(form.value.id ? '更新成功' : '创建成功')
        closeModal()
        await fetchList()
      } catch (error) {
        console.error('保存平台联系人失败:', error)
        alert(error.message || '保存失败，请稍后重试')
      } finally {
        saving.value = false
      }
    }

    const handlePageChange = (page) => {
      currentPage.value = page
      fetchList()
    }

    const handleStateFilterChange = () => {
      currentPage.value = 1
      fetchList()
    }

    const resetFilters = () => {
      filterState.value = ''
      currentPage.value = 1
      fetchList()
    }

    onMounted(fetchList)

    return {
      contactList,
      loading,
      saving,
      detailLoading,
      currentPage,
      totalPages,
      total,
      filterState,
      modalVisible,
      modalTitle,
      form,
      fileInputRef,
      uploadingImage,
      pickQrCodeUrl,
      pickStateLabel,
      formatDate,
      openCreateModal,
      openEditModal,
      closeModal,
      triggerQrUpload,
      handleSelectQrImage,
      handleSave,
      handlePageChange,
      handleStateFilterChange,
      resetFilters
    }
  }
})
